import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, Plus } from 'lucide-react';
import type { Feature } from '@outss/ledger-core';
import {
  BUSINESS_NAVIGATION,
  COMING_SOON,
  NAVIGATION,
  NEW_MENU,
  isAvailable,
} from '@/app/navigation';
import { OutssMark, Logo } from '@/components/brand/logo';
import { BusinessNewMenu } from '@/components/portal/business-new-menu';
import { useEInvoicingEnabled } from '@/lib/einvoicing';
import { useTrackingSettings } from '@/lib/tracking';
import { useOrgLocale } from '@/lib/org-locale';
import { openNewDrawer } from '@/lib/new-drawer';
import { navLabel, useLanguage, useT } from '@/lib/i18n';
import { useMessagesUnread } from '@/lib/messages';
import { featureShown, useBusinessView, useFeatureSet } from '@/lib/portal';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '@/components/ui/sidebar';

/** "+ New" button and four-column menu, as in QBO (document 05). */
function NewMenu() {
  const { taxPack } = useOrgLocale();
  const features = useFeatureSet();
  const lang = useLanguage();
  const t = useT();
  const L = (label: string) => navLabel(label, lang);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-10 w-full justify-start gap-2 rounded-xl shadow-sm shadow-brand-violet/25"
          size="lg"
        >
          <Plus className="size-4" strokeWidth={2.25} /> {t('common.new')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[80vh] w-[min(640px,calc(100vw-1.5rem))] overflow-y-auto p-4"
      >
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {NEW_MENU.map((col) => (
            <div key={col.heading}>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {L(col.heading)}
              </div>
              <ul className="space-y-1">
                {col.items
                  .filter(
                    (item) =>
                      (!item.pack || item.pack === taxPack) &&
                      featureShown(features, item.features),
                  )
                  .map((item) => (
                    <li key={item.label} title={isAvailable(item.to) ? undefined : L(COMING_SOON)}>
                      {!isAvailable(item.to) ? (
                        <DropdownMenuItem disabled className="px-1 py-1" data-unavailable>
                          {L(item.label)}
                        </DropdownMenuItem>
                      ) : item.drawer ? (
                        <DropdownMenuItem
                          className="px-1 py-1"
                          onSelect={() => openNewDrawer({ kind: item.drawer! })}
                        >
                          {L(item.label)}
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem asChild className="px-1 py-1">
                          <Link to={item.to}>{L(item.label)}</Link>
                        </DropdownMenuItem>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppSidebar({ isStaff = true }: { isStaff?: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const business = useBusinessView();
  const navigation = business.on ? BUSINESS_NAVIGATION : NAVIGATION;
  const flags = { einvoicing: useEInvoicingEnabled(), projects: useTrackingSettings().projects };
  const { taxPack } = useOrgLocale();
  const features = useFeatureSet();
  const unreadMessages = useMessagesUnread().data ?? 0;
  const lang = useLanguage();
  const t = useT();
  const L = (label: string) => navLabel(label, lang);
  const badges = { messages: unreadMessages };
  const shown = (item: {
    flag?: keyof typeof flags;
    pack?: string;
    features?: readonly Feature[];
  }) =>
    (!item.flag || flags[item.flag]) &&
    (!item.pack || item.pack === taxPack) &&
    featureShown(features, item.features);
  const matches = (to: string) =>
    to === '/'
      ? pathname === '/'
      : // Settings is one entry for every /settings page
        to.startsWith('/settings/')
        ? pathname.startsWith('/settings/')
        : pathname === to || pathname.startsWith(`${to}/`);
  // Within a group only the most specific item is active (Sales › Overview is "/sales").
  const isActive = (to: string, siblings?: Array<{ to: string }>) =>
    matches(to) &&
    !(siblings ?? []).some((o) => o.to !== to && o.to.startsWith(to) && matches(o.to));

  return (
    <Sidebar collapsible="icon" side={lang === 'ar' ? 'right' : 'left'}>
      <SidebarHeader className="gap-4 px-3 pt-4 pb-2">
        <div className="flex h-8 items-center px-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <Logo className="group-data-[collapsible=icon]:hidden" />
          <OutssMark className="hidden group-data-[collapsible=icon]:block" />
        </div>
        <div className="group-data-[collapsible=icon]:hidden">
          {business.on ? <BusinessNewMenu /> : <NewMenu />}
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup className="px-3 pt-1 group-data-[collapsible=icon]:px-2">
          <SidebarMenu>
            {navigation
              .filter((g) => (isStaff || !g.staffOnly) && shown(g))
              .map((group) =>
                group.items ? (
                  <Collapsible
                    key={group.label}
                    asChild
                    defaultOpen={group.items.some((i) => isActive(i.to))}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          tooltip={L(group.label)}
                          isActive={group.items.some((i) => isActive(i.to))}
                        >
                          <group.icon />
                          <span>{L(group.label)}</span>
                          <ChevronRight className="rtl:-scale-x-100 ms-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 rtl:group-data-[state=open]/collapsible:-rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {group.items.filter(shown).map((item) => (
                            <SidebarMenuSubItem
                              key={`${group.label}-${item.to}`}
                              title={isAvailable(item.to) ? undefined : L(COMING_SOON)}
                            >
                              {isAvailable(item.to) ? (
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActive(item.to, group.items)}
                                >
                                  <Link to={item.to}>
                                    <span>{L(item.label)}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              ) : (
                                <SidebarMenuSubButton asChild aria-disabled>
                                  <span data-unavailable>{L(item.label)}</span>
                                </SidebarMenuSubButton>
                              )}
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ) : (
                  <SidebarMenuItem key={group.label}>
                    <SidebarMenuButton
                      asChild
                      tooltip={L(group.label)}
                      isActive={isActive(group.to!)}
                    >
                      <Link to={group.to!}>
                        <group.icon />
                        <span>{L(group.label)}</span>
                      </Link>
                    </SidebarMenuButton>
                    {group.badge && badges[group.badge] ? (
                      <SidebarMenuBadge
                        className="bg-primary text-primary-foreground peer-hover/menu-button:text-primary-foreground peer-data-[active=true]/menu-button:text-primary-foreground"
                        aria-label={t('shell.unread', { count: badges[group.badge] })}
                        data-testid={`nav-badge-${group.badge}`}
                      >
                        {badges[group.badge] > 99 ? '99+' : badges[group.badge]}
                      </SidebarMenuBadge>
                    ) : null}
                  </SidebarMenuItem>
                ),
              )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
